import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router';
import { Client } from 'src/admin/users/types';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useClient } from '../hooks/useClient';
import { useClients } from '../hooks/useClients';

type CurrentClient = {
  clientId: string;
  client: Client | null;
  refetch: ReturnType<typeof useClient>['refetch'];
  isLoading: boolean;
  isClientPage: boolean;
};

type CurrentClientContextType = {
  isEnabled: true;
};

const CurrentClientContext = createContext<CurrentClientContextType | undefined>(undefined);

type CurrentClientProviderProps = {
  children: ReactNode;
};

export function CurrentClientProvider({ children }: CurrentClientProviderProps) {
  return <CurrentClientContext value={{ isEnabled: true }}>{children}</CurrentClientContext>;
}

export function useCurrentClientIfAvailable() {
  const context = useContext(CurrentClientContext);
  if (context === undefined) {
    throw new Error('useCurrentClientIfAvailable must be used within a CurrentClientProvider');
  }

  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const clientId = clientIdParam;
  const hasClientId = clientId != null && clientId !== 'undefined';

  const { flagEnabledPaginatedClients } = useFlags();

  const single = useClient(flagEnabledPaginatedClients && hasClientId ? clientId : undefined);
  const list = useClients({}, { enabled: !flagEnabledPaginatedClients });

  const client = flagEnabledPaginatedClients
    ? single.client
    : (list.clients.find(c => c.id === clientId) ?? null);
  const isLoading = flagEnabledPaginatedClients ? single.isLoading : list.isLoading;
  const refetch = (
    flagEnabledPaginatedClients ? single.refetch : list.refetch
  ) as CurrentClient['refetch'];

  return {
    isLoading,
    refetch,
    isClientPage: clientIdParam != null,
    clientId,
    client: hasClientId ? client : null,
  };
}

export function useCurrentClient(): CurrentClient {
  const data = useCurrentClientIfAvailable();

  if (hasClientId(data)) {
    return data as CurrentClient;
  }

  throw new Error('Client ID is null');
}

function hasClientId(data: Partial<CurrentClient>): data is CurrentClient {
  return data.clientId != null;
}
